import { defineStore, Store } from 'pinia'
import { FormContext, useForm } from 'vee-validate'
import {
    PersonalAccountData,
    EcspComplianceProfile,
    EsacComplianceProfile,
    getShowPostalAddressFields,
    usePersonalAccountDataState,
} from './useAccountDataPersonal'
import {
    navigateToNextPageWithMissingFieldsForInvestmentPersonal,
    PersonalAccountPage,
} from './useAccountNavigationPersonal'
import { getFieldRulesNatural } from '../../validation'
import { onMounted, onUnmounted } from 'vue'
import { openConfirmDialog } from '../useDialog'
import { i18n } from '../../localization'
import { isPastPepOptionShown, isRequireAdditionalPepInformationEnabled } from '../usePepSettings'

export interface PersonalAccountFormStore {
    currentForm: FormContext
    hasValidationErrors?: boolean
    isDirty?: boolean
    submit?: () => unknown
}

export const usePersonalAccountFormState = defineStore('personal-account-form', {
    state: () => {
        return {
            currentForm: null,
            missingDataProcessForVerificationActive: false,
        }
    },
    getters: {
        hasValidationErrors: (state) => (state.currentForm ? Object.keys(state.currentForm.errors).length > 0 : false),
        isDirty: (state) => state.currentForm && state.currentForm.meta.dirty,
    },
    actions: {
        async submit() {
            const personalAccountStore = usePersonalAccountDataState()
            const { valid } = await this.currentForm.validate()

            if (valid) {
                const savedData = await personalAccountStore.saveAccountData()
                // when missingDataProcessForVerificationActive is set to true the user will be redirected to the next page with missing fields for investment an then back to verification
                if (this.missingDataProcessForVerificationActive) {
                    await navigateToNextPageWithMissingFieldsForInvestmentPersonal()
                }
                return savedData
            }
        },
    },
})

export async function handleDirtyState(
    accountStore: Store<'personal-account-data', PersonalAccountData>
): Promise<boolean> {
    const { currentForm } = usePersonalAccountFormState()

    return new Promise(async (resolve) => {
        let resolveVal = true
        let confirmed = false

        if (currentForm && currentForm.meta.dirty) {
            confirmed = await openConfirmDialog(
                i18n.global.t('dialog.formDirty.title'),
                i18n.global.t('dialog.formDirty.text'),
                {
                    type: 'Warning',
                    confirmText: i18n.global.t('dialog.formDirty.confirm'),
                    cancelText: i18n.global.t('dialog.formDirty.cancel'),
                }
            )
            if (confirmed) {
                if (!currentForm.meta.valid) {
                    openConfirmDialog(
                        i18n.global.t('dialog.formInvalid.title'),
                        i18n.global.t('dialog.formInvalid.text'),
                        {
                            type: 'Error',
                            hideCancel: true,
                        }
                    )
                    resolveVal = false
                } else {
                    accountStore.saveAccountData().catch(() => (resolveVal = false)) // TODO: maybe add additional tailwind alert
                }
            } else {
                currentForm.resetForm()

                const ecspKeys = Object.getOwnPropertyNames(new EcspComplianceProfile())
                const esacKeys = Object.getOwnPropertyNames(new EsacComplianceProfile())
                const formValuesKeys = Object.getOwnPropertyNames(currentForm.values)

                if (currentForm.values.account) {
                    Object.keys(currentForm.values.account).forEach((key) => {
                        accountStore.$patch({ naturalProfile: { [key]: currentForm.values.account[key] } })
                    })
                }

                if (JSON.stringify(ecspKeys) === JSON.stringify(formValuesKeys)) {
                    Object.keys(currentForm.values).forEach((key) => {
                        accountStore.$patch({
                            naturalProfile: {
                                compliance_profiles: {
                                    ecsp: { [key]: { ...currentForm.values[key] } },
                                },
                            },
                        })
                    })
                }

                if (JSON.stringify(esacKeys) === JSON.stringify(formValuesKeys)) {
                    Object.keys(currentForm.values).forEach((key) => {
                        accountStore.$patch({
                            naturalProfile: {
                                compliance_profiles: {
                                    esac: { [key]: { ...currentForm.values[key] } },
                                },
                            },
                        })
                    })
                }
            }
        }
        resolve(resolveVal)
    })
}

export function getPersonalAccountValidationSchema(validateInvestmentRequired = false) {
    const accountValidationSchema: {
        // @ts-ignore
        [key: PersonalAccountPage]: Record<string, string>
    } = {
        'Personal Data': {
            'account.first_name': getFieldRulesNatural('first_name', ['min:2'], validateInvestmentRequired),
            'account.last_name': getFieldRulesNatural('last_name', ['min:2'], validateInvestmentRequired),
            'account.date_of_birth': getFieldRulesNatural(
                'date_of_birth',
                ['date', 'min_age:18', 'max_age:100'],
                validateInvestmentRequired
            ),
            'account.nationality': getFieldRulesNatural('nationality', ['countryISO3166'], validateInvestmentRequired),
            'account.gender': getFieldRulesNatural('gender', [], validateInvestmentRequired),
            'account.country_of_birth': getFieldRulesNatural('country_of_birth', [], validateInvestmentRequired),
            'account.city_of_birth': getFieldRulesNatural('city_of_birth', [], validateInvestmentRequired),
            'account.pep_past_position': isRequireAdditionalPepInformationEnabled()
                ? 'required_if:account.pep_past'
                : '',
            'account.pep_position':
                isRequireAdditionalPepInformationEnabled() && isPastPepOptionShown() ? 'required_if:account.pep' : '',
        },
        'Address Data': {
            'account.address': getFieldRulesNatural(
                'address',
                ['required', 'min:4', 'max:255'],
                validateInvestmentRequired
            ),
            'account.postal_code': getFieldRulesNatural(
                'postal_code',
                ['min:2', 'max:255'],
                validateInvestmentRequired
            ),
            'account.city': getFieldRulesNatural('city', ['min:2', 'max:255'], validateInvestmentRequired),
            'account.state': getFieldRulesNatural('state', ['min:2', 'max:255'], validateInvestmentRequired),
            'account.country': getFieldRulesNatural('country', ['countryISO3166'], validateInvestmentRequired),
            'account.postal_address': getShowPostalAddressFields()
                ? getFieldRulesNatural('postal_address', ['min:4', 'max:255'], validateInvestmentRequired)
                : '',
            'account.postal_address_postal_code': getShowPostalAddressFields()
                ? getFieldRulesNatural('postal_address_postal_code', ['min:2', 'max:255'], validateInvestmentRequired)
                : '',
            'account.postal_address_city': getShowPostalAddressFields()
                ? getFieldRulesNatural('postal_address_city', ['min:2', 'max:255'], validateInvestmentRequired)
                : '',
            'account.postal_address_state': getShowPostalAddressFields()
                ? getFieldRulesNatural('postal_address_state', ['min:2', 'max:255'], validateInvestmentRequired)
                : '',
            'account.postal_address_country': getShowPostalAddressFields()
                ? getFieldRulesNatural('postal_address_country', ['countryISO3166'], validateInvestmentRequired)
                : '',
        },
        'Contact Data': {
            'account.email': null,
            'account.phone_number': getFieldRulesNatural('phone_number', [], validateInvestmentRequired),
            'account.mobile_number': getFieldRulesNatural('mobile_number', [], validateInvestmentRequired),
            'account.email_pending': getFieldRulesNatural(
                'email_pending',
                ['email', `not_equals:@account.email,${i18n.global.t('validation_error.new_email_not_equals_old')}`],
                validateInvestmentRequired
            ),
        },
        'Legal Information': {
            'account.highest_educational_attainment': getFieldRulesNatural(
                'highest_educational_attainment',
                [],
                validateInvestmentRequired
            ),
            'account.current_occupation': getFieldRulesNatural('current_occupation', [], validateInvestmentRequired),
            'account.experience_without_advice': getFieldRulesNatural(
                'experience_without_advice',
                [],
                validateInvestmentRequired
            ),
            'account.experience_with_investment_advice': getFieldRulesNatural(
                'experience_with_investment_advice',
                [],
                validateInvestmentRequired
            ),
            'account.experience_with_asset_management': getFieldRulesNatural(
                'experience_with_asset_management',
                [],
                validateInvestmentRequired
            ),
            'account.experience_share': getFieldRulesNatural('experience_share', [], validateInvestmentRequired),
            'account.experience_bond': getFieldRulesNatural('experience_bond', [], validateInvestmentRequired),
            'account.experience_participation_paper': getFieldRulesNatural(
                'experience_participation_paper',
                [],
                validateInvestmentRequired
            ),
            'account.experience_crypto_currency': getFieldRulesNatural(
                'experience_crypto_currency',
                [],
                validateInvestmentRequired
            ),
            'account.experience_unsecured_junior_debt': getFieldRulesNatural(
                'experience_unsecured_junior_debt',
                [],
                validateInvestmentRequired
            ),
            'account.knowledge_share': getFieldRulesNatural('knowledge_share', [], validateInvestmentRequired),
            'account.knowledge_bond': getFieldRulesNatural('knowledge_bond', [], validateInvestmentRequired),
            'account.knowledge_participation_paper': getFieldRulesNatural(
                'knowledge_participation_paper',
                [],
                validateInvestmentRequired
            ),
            'account.knowledge_crypto_currency': getFieldRulesNatural(
                'knowledge_crypto_currency',
                [],
                validateInvestmentRequired
            ),
            'account.knowledge_unsecured_junior_debt': getFieldRulesNatural(
                'knowledge_unsecured_junior_debt',
                [],
                validateInvestmentRequired
            ),
            'account.average_investment_transactions_per_year': getFieldRulesNatural(
                'average_investment_transactions_per_year',
                [],
                validateInvestmentRequired
            ),
            'account.average_investment_transaction_amount': getFieldRulesNatural(
                'average_investment_transaction_amount',
                [],
                validateInvestmentRequired
            ),
        },
        'Verification': {
            'account.pep': getFieldRulesNatural('pep', [], validateInvestmentRequired),
        },
        'Tax Data': {
            'account.social_security_number': getFieldRulesNatural(
                'social_security_number',
                [],
                validateInvestmentRequired
            ),
            'account.tax_number': getFieldRulesNatural('tax_number', [], validateInvestmentRequired),
        },
    }

    return accountValidationSchema
}

export function initPersonalAccountForm(page: PersonalAccountPage) {
    const { naturalProfile: account } = usePersonalAccountDataState()

    const form = useForm({
        validationSchema: getPersonalAccountValidationSchema()[page],
        initialValues: { account },
    })
    const formState = usePersonalAccountFormState()

    onMounted(() => {
        // @ts-ignore
        formState.currentForm = form
        formState.currentForm.resetForm()
    })
    onUnmounted(() => {
        formState.currentForm = null
    })
}
